import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  DollarSign,
  Award,
  Trophy,
  RefreshCcw,
  Settings,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Target,
  Percent,
  Building2,
  PieChart,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { BASE_URL as API_URL } from '../utils/apiUrl';

// Get current period code
const getCurrentPeriodCode = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Performance band color mapping
const getBandColor = (band) => {
  const colors = {
    'A+': 'bg-emerald-600',
    'A': 'bg-emerald-500',
    'B+': 'bg-[#C5A059]',
    'B': 'bg-amber-500',
    'C': 'bg-orange-500',
    'D': 'bg-red-500',
  };
  return colors[band] || 'bg-slate-500';
};

// Trend icon component
const TrendIcon = ({ trend }) => {
  if (trend === 'UP') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (trend === 'DOWN') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

// KPI Card component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, format = 'number' }) => {
  const formatValue = (val) => {
    if (format === 'currency') return `₹${val?.toLocaleString() || 0}`;
    if (format === 'percent') return `${val?.toFixed(1) || 0}%`;
    return val?.toLocaleString() || 0;
  };

  return (
    <Card className="card-dark border-white/5">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{formatValue(value)}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {trend && <TrendIcon trend={trend} />}
            <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#C5A059]" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Ring component
const ProgressRing = ({ value, max = 100, size = 80, strokeWidth = 8, color = '#C5A059' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percent = Math.min((value / max) * 100, 100);
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="transparent"
          className="stroke-slate-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="transparent"
          stroke={color}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-white">{percent.toFixed(0)}%</span>
      </div>
    </div>
  );
};

// Format currency for display
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value.toLocaleString('en-IN')}`;
};

// Empty State Component
const EmptyState = ({ title, message, icon: Icon }) => (
  <Card className="card-dark border-slate-700">
    <CardContent className="py-12 text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm max-w-md mx-auto">{message}</p>
    </CardContent>
  </Card>
);

// Simple Chart Component (supports line and bar)
const SimpleChart = ({ data, dataKey, color = '#C5A059', type = 'line' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-500">
        No trend data available
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  const minValue = 0;
  const range = maxValue - minValue || 1;
  
  if (type === 'bar') {
    return (
      <div className="h-48 flex items-end justify-between gap-1 px-2">
        {data.map((item, idx) => {
          const height = ((item[dataKey] || 0) - minValue) / range * 100;
          return (
            <div key={idx} className="flex flex-col items-center flex-1">
              <div 
                className="w-full rounded-t transition-all hover:opacity-80"
                style={{ 
                  height: `${Math.max(height, 2)}%`, 
                  backgroundColor: color,
                  minHeight: '4px'
                }}
                title={`${item.month_label}: ${formatCurrency(item[dataKey])}`}
              />
              <span className="text-[10px] text-slate-500 mt-1 rotate-45 origin-left">
                {item.month_label?.split(' ')[0]}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  
  // Line chart
  const points = data.map((item, idx) => {
    const x = (idx / (data.length - 1)) * 100;
    const y = 100 - ((item[dataKey] || 0) - minValue) / range * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="h-48 relative">
      <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {data.map((item, idx) => {
          const x = (idx / (data.length - 1)) * 100;
          const y = 100 - ((item[dataKey] || 0) - minValue) / range * 100;
          return (
            <circle
              key={idx}
              cx={x}
              cy={y}
              r="3"
              fill={color}
              className="cursor-pointer"
            >
              <title>{`${item.month_label}: ${formatCurrency(item[dataKey])}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-slate-500">
        {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0).map((item, idx) => (
          <span key={idx}>{item.month_label?.split(' ')[0]}</span>
        ))}
      </div>
    </div>
  );
};

const PerformancePage = () => {
  const { user, token, hasRole } = useAuth();
  const isManagement = hasRole(['principal', 'business_head']);
  const isDealer = user?.role === 'dealer';
  const isPrincipal = user?.role === 'principal';
  const isRM = user?.role === 'rm';

  // State
  const [loading, setLoading] = useState(true);
  const [periodCode, setPeriodCode] = useState(getCurrentPeriodCode());
  const [lookbackMonths, setLookbackMonths] = useState(12);
  const [kpis, setKpis] = useState(null);
  const [firmKpis, setFirmKpis] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [myScorecard, setMyScorecard] = useState(null);
  const [config, setConfig] = useState(null);
  const [trends, setTrends] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [newWeights, setNewWeights] = useState({
    brokerage: 40,
    aum: 30,
    clients: 20,
    retention: 10,
  });

  // RM Dashboard State
  const [rmDashboard, setRmDashboard] = useState(null);
  const [rmDealers, setRmDealers] = useState([]);
  const [rmTrends, setRmTrends] = useState([]);
  const [rmActiveTab, setRmActiveTab] = useState('overview');

  // Principal Dashboard State - RM & Dealer Performance
  const [allRmsPerformance, setAllRmsPerformance] = useState([]);
  const [allDealersPerformance, setAllDealersPerformance] = useState([]);
  const [selectedRmForDrilldown, setSelectedRmForDrilldown] = useState(null);
  const [selectedDealerForDrilldown, setSelectedDealerForDrilldown] = useState(null);
  const [drilldownTrends, setDrilldownTrends] = useState([]);
  const [showDrilldownModal, setShowDrilldownModal] = useState(false);
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'

  // Fetch functions
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setNewWeights({
          brokerage: data.scorecard_weights.brokerage * 100,
          aum: data.scorecard_weights.aum * 100,
          clients: data.scorecard_weights.clients * 100,
          retention: data.scorecard_weights.retention * 100,
        });
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, [token]);

  const fetchKpis = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/kpis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setKpis(data);
      }
    } catch (err) {
      console.error('Error fetching KPIs:', err);
    }
  }, [token]);

  const fetchFirmKpis = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/firm-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFirmKpis(data);
      }
    } catch (err) {
      console.error('Error fetching firm KPIs:', err);
    }
  }, [token]);

  const fetchScorecards = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/scorecards?period_code=${periodCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setScorecards(data);
        if (isDealer && data.length > 0) {
          setMyScorecard(data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching scorecards:', err);
    }
  }, [token, periodCode, isDealer]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/leaderboard?period_code=${periodCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  }, [token, periodCode]);

  const fetchMyRank = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/my-rank?period_code=${periodCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyRank(data);
      }
    } catch (err) {
      console.error('Error fetching my rank:', err);
    }
  }, [token, periodCode]);

  const fetchTrends = useCallback(async () => {
    try {
      const scope = isDealer ? 'dealer' : 'firm';
      const url = isDealer 
        ? `${API_URL}/api/performance/trends?scope=dealer&lookback_months=${lookbackMonths}`
        : `${API_URL}/api/performance/trends?scope=firm&lookback_months=${lookbackMonths}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTrends(data);
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  }, [token, isDealer, lookbackMonths]);

  // RM Dashboard Fetch Functions
  const fetchRmDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/rm-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRmDashboard(data);
        // Also fetch dealers and trends if we have an rm_code
        if (data?.rm_code) {
          // Fetch dealers
          const dealersRes = await fetch(`${API_URL}/api/performance/rm/${data.rm_code}/dealers`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (dealersRes.ok) {
            const dealersData = await dealersRes.json();
            setRmDealers(dealersData.dealers || []);
          }
          // Fetch trends
          const trendsRes = await fetch(`${API_URL}/api/performance/rm/${data.rm_code}/trends?months=12`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (trendsRes.ok) {
            const trendsData = await trendsRes.json();
            setRmTrends(trendsData.trends || []);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching RM dashboard:', err);
    }
  }, [token]);

  const fetchRmDealers = useCallback(async (rmCode) => {
    try {
      const res = await fetch(`${API_URL}/api/performance/rm/${rmCode}/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRmDealers(data.dealers || []);
      }
    } catch (err) {
      console.error('Error fetching RM dealers:', err);
    }
  }, [token]);

  const fetchRmTrends = useCallback(async (rmCode) => {
    try {
      const res = await fetch(`${API_URL}/api/performance/rm/${rmCode}/trends?months=12`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRmTrends(data.trends || []);
      }
    } catch (err) {
      console.error('Error fetching RM trends:', err);
    }
  }, [token]);

  // Principal Dashboard Fetch Functions
  const fetchAllRmsPerformance = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/all-rms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllRmsPerformance(data.rms || []);
      }
    } catch (err) {
      console.error('Error fetching all RMs performance:', err);
    }
  }, [token]);

  const fetchAllDealersPerformance = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/all-dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllDealersPerformance(data.dealers || []);
      }
    } catch (err) {
      console.error('Error fetching all dealers performance:', err);
    }
  }, [token]);

  const fetchDrilldownTrends = useCallback(async (type, code) => {
    try {
      const endpoint = type === 'rm' 
        ? `${API_URL}/api/performance/rm/${code}/trends?months=12`
        : `${API_URL}/api/performance/dealer/${code}/trends?months=12`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDrilldownTrends(data.trends || []);
      }
    } catch (err) {
      console.error('Error fetching drilldown trends:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      if (isRM) {
        // RM-specific data loading
        await fetchRmDashboard();
      } else if (isManagement) {
        await Promise.all([
          fetchConfig(),
          fetchFirmKpis(),
          fetchScorecards(),
          fetchLeaderboard(),
          fetchTrends(),
          fetchAllRmsPerformance(),
          fetchAllDealersPerformance(),
        ]);
      } else if (isDealer) {
        await Promise.all([
          fetchKpis(),
          fetchScorecards(),
          fetchMyRank(),
          fetchTrends(),
        ]);
      }
      
      setLoading(false);
    };
    loadData();
  }, [isManagement, isDealer, isRM, fetchConfig, fetchFirmKpis, fetchScorecards, fetchLeaderboard, fetchKpis, fetchMyRank, fetchTrends, fetchRmDashboard, fetchAllRmsPerformance, fetchAllDealersPerformance]);

  // Actions
  const handleGenerateScorecards = async () => {
    try {
      const res = await fetch(`${API_URL}/api/performance/scorecards/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ period_code: periodCode }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated ${data.generated} scorecards`);
        fetchScorecards();
        fetchLeaderboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate scorecards');
      }
    } catch (err) {
      toast.error('Error generating scorecards');
    }
  };

  const handleUpdateConfig = async () => {
    const total = newWeights.brokerage + newWeights.aum + newWeights.clients + newWeights.retention;
    if (Math.abs(total - 100) > 0.1) {
      toast.error(`Weights must sum to 100% (currently ${total}%)`);
      return;
    }
    if (newWeights.brokerage > 40) {
      toast.error('Brokerage weight cannot exceed 40%');
      return;
    }
    if (newWeights.retention < 10) {
      toast.error('Retention weight must be at least 10%');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/performance/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          brokerage: newWeights.brokerage / 100,
          aum: newWeights.aum / 100,
          clients: newWeights.clients / 100,
          retention: newWeights.retention / 100,
        }),
      });
      if (res.ok) {
        toast.success('Configuration updated');
        setShowConfigDialog(false);
        fetchConfig();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update config');
      }
    } catch (err) {
      toast.error('Error updating config');
    }
  };

  // Handle RM drilldown
  const handleRmDrilldown = async (rm) => {
    setSelectedRmForDrilldown(rm);
    setSelectedDealerForDrilldown(null);
    await fetchDrilldownTrends('rm', rm.rm_code);
    setShowDrilldownModal(true);
  };

  // Handle Dealer drilldown
  const handleDealerDrilldown = async (dealer) => {
    setSelectedDealerForDrilldown(dealer);
    setSelectedRmForDrilldown(null);
    await fetchDrilldownTrends('dealer', dealer.dealer_code);
    setShowDrilldownModal(true);
  };

  // Render RM View
  const renderRMView = () => {
    if (!rmDashboard) {
      return (
        <EmptyState 
          title="RM Account Not Linked"
          message="Your user account is not linked to an RM Master record. Please contact the Principal to link your account."
          icon={Users}
        />
      );
    }

    return (
      <div className="space-y-6">
        {/* RM Tabs */}
        <Tabs value={rmActiveTab} onValueChange={setRmActiveTab}>
          <TabsList className="bg-slate-800/50 border border-white/10">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
              Overview
            </TabsTrigger>
            <TabsTrigger value="dealers" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
              Dealers Under Me
            </TabsTrigger>
            <TabsTrigger value="my-performance" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
              My Performance
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="card-dark">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Dealers</p>
                      <p className="text-2xl font-bold text-white">{rmDashboard.overview?.total_dealers || 0}</p>
                    </div>
                    <Building2 className="w-8 h-8 text-[#C5A059]" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Clients</p>
                      <p className="text-2xl font-bold text-white">{rmDashboard.overview?.total_clients || 0}</p>
                      <p className="text-xs text-slate-500">{rmDashboard.overview?.rm_direct_clients || 0} direct</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Aggregated AUM</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(rmDashboard.overview?.current_aum)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Target Achievement</p>
                      <p className="text-2xl font-bold text-white">{rmDashboard.overview?.target_achievement_percent || 0}%</p>
                    </div>
                    <Target className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="card-dark">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">MTD Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#C5A059]">{formatCurrency(rmDashboard.overview?.mtd_revenue)}</p>
                  <p className="text-sm text-slate-400 mt-1">Month to Date</p>
                </CardContent>
              </Card>
              <Card className="card-dark">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">YTD Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-emerald-500">{formatCurrency(rmDashboard.overview?.ytd_revenue)}</p>
                  <p className="text-sm text-slate-400 mt-1">Year to Date</p>
                </CardContent>
              </Card>
            </div>

            {/* Trends Chart */}
            <Card className="card-dark">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Revenue Trend (12 Months)</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={chartType === 'line' ? 'default' : 'outline'}
                      onClick={() => setChartType('line')}
                      className={chartType === 'line' ? 'bg-[#C5A059]' : ''}
                    >
                      Line
                    </Button>
                    <Button
                      size="sm"
                      variant={chartType === 'bar' ? 'default' : 'outline'}
                      onClick={() => setChartType('bar')}
                      className={chartType === 'bar' ? 'bg-[#C5A059]' : ''}
                    >
                      Bar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {rmTrends.length > 0 ? (
                  <SimpleChart data={rmTrends} dataKey="revenue" label="Revenue" type={chartType} />
                ) : (
                  <div className="h-48 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No trend data available yet</p>
                      <p className="text-xs">Import brokerage data to see trends</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dealers Under Me Tab */}
          <TabsContent value="dealers" className="space-y-4">
            {rmDealers.length === 0 ? (
              <EmptyState 
                title="No Dealers Assigned"
                message="No dealers are currently reporting to you. Dealers can be assigned via the Dealer Master page."
                icon={Building2}
              />
            ) : (
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white">Dealers Under My Supervision ({rmDealers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-400 font-medium">Dealer</th>
                          <th className="text-right p-3 text-slate-400 font-medium">Active Clients</th>
                          <th className="text-right p-3 text-slate-400 font-medium">AUM</th>
                          <th className="text-right p-3 text-slate-400 font-medium">MTD Revenue</th>
                          <th className="text-right p-3 text-slate-400 font-medium">YTD Revenue</th>
                          <th className="text-right p-3 text-slate-400 font-medium">Target %</th>
                          <th className="text-center p-3 text-slate-400 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rmDealers.map((dealer) => (
                          <tr 
                            key={dealer.dealer_code} 
                            className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => handleDealerDrilldown(dealer)}
                          >
                            <td className="p-3">
                              <div>
                                <p className="text-white font-medium">{dealer.name}</p>
                                <p className="text-xs text-slate-500">{dealer.dealer_code}</p>
                              </div>
                            </td>
                            <td className="text-right p-3 text-white">{dealer.active_clients || 0}</td>
                            <td className="text-right p-3 text-white">{formatCurrency(dealer.current_aum)}</td>
                            <td className="text-right p-3 text-[#C5A059]">{formatCurrency(dealer.mtd_revenue)}</td>
                            <td className="text-right p-3 text-emerald-500">{formatCurrency(dealer.ytd_revenue)}</td>
                            <td className="text-right p-3">
                              <span className={`font-medium ${dealer.achievement_percent >= 100 ? 'text-emerald-500' : dealer.achievement_percent >= 75 ? 'text-[#C5A059]' : 'text-red-500'}`}>
                                {dealer.achievement_percent || 0}%
                              </span>
                            </td>
                            <td className="text-center p-3">
                              <Badge variant={dealer.status === 'Active' ? 'default' : 'secondary'}>
                                {dealer.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* My Performance Tab */}
          <TabsContent value="my-performance" className="space-y-4">
            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="text-white">My Individual Performance</CardTitle>
                <CardDescription>Your direct client performance (excluding dealers)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-400">My Direct Clients</p>
                    <p className="text-2xl font-bold text-white">{rmDashboard.overview?.rm_direct_clients || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-400">My MTD Revenue</p>
                    <p className="text-2xl font-bold text-[#C5A059]">
                      {rmDashboard.overview?.mtd_revenue && rmDashboard.overview?.total_dealers > 0
                        ? 'Aggregated'
                        : formatCurrency(rmDashboard.overview?.mtd_revenue)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-400">My YTD Revenue</p>
                    <p className="text-2xl font-bold text-emerald-500">
                      {rmDashboard.overview?.ytd_revenue && rmDashboard.overview?.total_dealers > 0
                        ? 'Aggregated'
                        : formatCurrency(rmDashboard.overview?.ytd_revenue)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-400">My Target %</p>
                    <p className="text-2xl font-bold text-purple-500">{rmDashboard.overview?.target_achievement_percent || 0}%</p>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Note: Revenue data is aggregated. Individual RM vs Dealer breakdown will be available after brokerage import.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  // Render Dealer View
  const renderDealerView = () => (
    <div className="space-y-6">
      {/* My Rank Card */}
      {myRank && (
        <Card className="card-dark border-[#C5A059]/30 bg-gradient-to-br from-slate-900 to-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">#{myRank.rank || '-'}</div>
                  <p className="text-sm text-slate-400">of {myRank.total_dealers} dealers</p>
                </div>
                <div className="h-16 w-px bg-slate-700" />
                <div className="text-center">
                  <Badge className={`${getBandColor(myRank.band)} text-white text-lg px-4 py-1`}>
                    {myRank.band || 'N/A'}
                  </Badge>
                  <p className="text-sm text-slate-400 mt-2">Performance Band</p>
                </div>
                <div className="h-16 w-px bg-slate-700" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#C5A059]">{myRank.score?.toFixed(1) || 0}</div>
                  <p className="text-sm text-slate-400">Composite Score</p>
                </div>
              </div>
              <Trophy className="w-16 h-16 text-[#C5A059]/30" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="MTD Brokerage"
            value={kpis.brokerage?.mtd}
            subtitle={`Growth: ${kpis.brokerage?.growth_percent}%`}
            icon={DollarSign}
            format="currency"
            trend={kpis.brokerage?.growth_percent > 0 ? 'UP' : kpis.brokerage?.growth_percent < 0 ? 'DOWN' : 'STABLE'}
          />
          <KPICard
            title="Current AUM"
            value={kpis.aum?.current}
            subtitle={`Growth: ${kpis.aum?.growth_percent}%`}
            icon={TrendingUp}
            format="currency"
            trend={kpis.aum?.growth_percent > 0 ? 'UP' : kpis.aum?.growth_percent < 0 ? 'DOWN' : 'STABLE'}
          />
          <KPICard
            title="Active Clients"
            value={kpis.clients?.active}
            subtitle={`Total: ${kpis.clients?.total}`}
            icon={Users}
          />
          <KPICard
            title="Client Retention"
            value={kpis.retention?.client_retention_rate}
            subtitle="Quarterly"
            icon={Award}
            format="percent"
          />
        </div>
      )}

      {/* Scorecard Details */}
      {myScorecard && (
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white">My Scorecard - {periodCode}</CardTitle>
            <CardDescription>Target vs Achievement breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Brokerage */}
              <div className="text-center">
                <ProgressRing 
                  value={myScorecard.achievement?.brokerage_percent || 0} 
                  color={myScorecard.achievement?.brokerage_percent >= 100 ? '#10b981' : '#C5A059'}
                />
                <p className="text-sm text-slate-400 mt-2">Brokerage</p>
                <p className="text-xs text-slate-500">
                  ₹{myScorecard.actuals?.brokerage?.toLocaleString()} / ₹{myScorecard.targets?.brokerage?.toLocaleString()}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendIcon trend={myScorecard.trends?.brokerage} />
                </div>
              </div>

              {/* AUM */}
              <div className="text-center">
                <ProgressRing 
                  value={myScorecard.achievement?.aum_percent || 0}
                  color={myScorecard.achievement?.aum_percent >= 100 ? '#10b981' : '#C5A059'}
                />
                <p className="text-sm text-slate-400 mt-2">AUM</p>
                <p className="text-xs text-slate-500">
                  ₹{myScorecard.actuals?.aum?.toLocaleString()} / ₹{myScorecard.targets?.aum?.toLocaleString()}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendIcon trend={myScorecard.trends?.aum} />
                </div>
              </div>

              {/* Clients */}
              <div className="text-center">
                <ProgressRing 
                  value={myScorecard.achievement?.active_clients_percent || 0}
                  color={myScorecard.achievement?.active_clients_percent >= 100 ? '#10b981' : '#C5A059'}
                />
                <p className="text-sm text-slate-400 mt-2">Active Clients</p>
                <p className="text-xs text-slate-500">
                  {myScorecard.actuals?.active_clients} / {myScorecard.targets?.active_clients}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendIcon trend={myScorecard.trends?.clients} />
                </div>
              </div>

              {/* Retention */}
              <div className="text-center">
                <ProgressRing 
                  value={myScorecard.achievement?.retention_rate_percent || 0}
                  color={myScorecard.achievement?.retention_rate_percent >= 100 ? '#10b981' : '#C5A059'}
                />
                <p className="text-sm text-slate-400 mt-2">Retention</p>
                <p className="text-xs text-slate-500">
                  {myScorecard.actuals?.retention_rate?.toFixed(1)}% / {myScorecard.targets?.retention_rate}%
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendIcon trend={myScorecard.trends?.retention} />
                </div>
              </div>
            </div>

            {/* Weighted Breakdown */}
            <div className="mt-6 pt-6 border-t border-white/5">
              <h4 className="text-sm font-medium text-white mb-4">Weighted Score Breakdown</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Brokerage (40%)</span>
                  <span className="text-sm text-white">{myScorecard.composite?.brokerage_weighted?.toFixed(1)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">AUM (30%)</span>
                  <span className="text-sm text-white">{myScorecard.composite?.aum_weighted?.toFixed(1)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Clients (20%)</span>
                  <span className="text-sm text-white">{myScorecard.composite?.clients_weighted?.toFixed(1)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Retention (10%)</span>
                  <span className="text-sm text-white">{myScorecard.composite?.retention_weighted?.toFixed(1)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-sm font-medium text-white">Total Score</span>
                  <span className="text-lg font-bold text-[#C5A059]">{myScorecard.composite?.total_score?.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render Management View
  const renderManagementView = () => (
    <Tabs defaultValue="dashboard" className="space-y-6">
      <TabsList className="bg-slate-800 flex-wrap">
        <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#C5A059]">
          Firm Dashboard
        </TabsTrigger>
        <TabsTrigger value="rm-performance" className="data-[state=active]:bg-[#C5A059]">
          RM Performance
        </TabsTrigger>
        <TabsTrigger value="dealer-performance" className="data-[state=active]:bg-[#C5A059]">
          Dealer Performance
        </TabsTrigger>
        <TabsTrigger value="scorecards" className="data-[state=active]:bg-[#C5A059]">
          Scorecards
        </TabsTrigger>
        <TabsTrigger value="leaderboard" className="data-[state=active]:bg-[#C5A059]">
          Leaderboard
        </TabsTrigger>
      </TabsList>

      {/* Firm Dashboard Tab */}
      <TabsContent value="dashboard" className="space-y-6">
        {firmKpis && (
          <>
            {/* Top KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="Firm Total AUM"
                value={firmKpis.aum?.total}
                icon={Building2}
                format="currency"
              />
              <KPICard
                title="MTD Brokerage"
                value={firmKpis.brokerage?.mtd}
                subtitle={`YTD: ₹${firmKpis.brokerage?.ytd?.toLocaleString()}`}
                icon={DollarSign}
                format="currency"
              />
              <KPICard
                title="Total Clients"
                value={firmKpis.clients?.total}
                subtitle={`Active: ${firmKpis.clients?.active}`}
                icon={Users}
              />
              <KPICard
                title="Activation Rate"
                value={firmKpis.clients?.activation_rate}
                icon={Target}
                format="percent"
              />
            </div>

            {/* Product Mix & Concentration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Product Mix */}
              <Card className="card-dark border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-[#C5A059]" />
                    Product Mix
                  </CardTitle>
                  <CardDescription>Brokerage breakdown by segment</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Equity', value: firmKpis.product_mix?.equity_percent, color: 'bg-emerald-500' },
                      { label: 'F&O', value: firmKpis.product_mix?.fo_percent, color: 'bg-blue-500' },
                      { label: 'Currency', value: firmKpis.product_mix?.currency_percent, color: 'bg-violet-500' },
                      { label: 'Mutual Fund', value: firmKpis.product_mix?.mf_percent, color: 'bg-amber-500' },
                      { label: 'IPO', value: firmKpis.product_mix?.ipo_percent, color: 'bg-rose-500' },
                    ].map((item) => (
                      <div key={item.label} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">{item.label}</span>
                          <span className="text-white">{item.value?.toFixed(1) || 0}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${item.color} transition-all duration-500`}
                            style={{ width: `${item.value || 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Concentration Risk */}
              <Card className="card-dark border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Concentration Risk
                  </CardTitle>
                  <CardDescription>AUM concentration metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-400">Top 10 Clients</span>
                        <span className="text-sm text-white">{firmKpis.concentration?.top_10_percent?.toFixed(1)}%</span>
                      </div>
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            firmKpis.concentration?.top_10_percent > 50 ? 'bg-red-500' : 'bg-[#C5A059]'
                          }`}
                          style={{ width: `${firmKpis.concentration?.top_10_percent || 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-400">Top 20 Clients</span>
                        <span className="text-sm text-white">{firmKpis.concentration?.top_20_percent?.toFixed(1)}%</span>
                      </div>
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 transition-all duration-500"
                          style={{ width: `${firmKpis.concentration?.top_20_percent || 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-400">Max Single Client</span>
                        <span className="text-sm text-white">{firmKpis.concentration?.max_single_client_percent?.toFixed(1)}%</span>
                      </div>
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            firmKpis.concentration?.max_single_client_percent > 20 ? 'bg-red-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${firmKpis.concentration?.max_single_client_percent || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </TabsContent>

      {/* RM Performance Tab */}
      <TabsContent value="rm-performance" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">RM Performance Overview</h2>
          <Button variant="outline" size="sm" onClick={fetchAllRmsPerformance}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {allRmsPerformance.length === 0 ? (
          <EmptyState 
            title="No RMs Found"
            message="No RM Master records exist yet. Create RMs in the RM Master page to track their performance."
            icon={Users}
          />
        ) : (
          <Card className="card-dark">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left p-4 text-slate-400 font-medium">RM Name</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Dealers</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Active Clients</th>
                      <th className="text-right p-4 text-slate-400 font-medium">AUM</th>
                      <th className="text-right p-4 text-slate-400 font-medium">MTD Revenue</th>
                      <th className="text-right p-4 text-slate-400 font-medium">YTD Revenue</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Target %</th>
                      <th className="text-center p-4 text-slate-400 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRmsPerformance.map((rm) => (
                      <tr 
                        key={rm.rm_code} 
                        className="border-b border-slate-800 hover:bg-slate-800/50"
                      >
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{rm.name}</p>
                            <p className="text-xs text-slate-500">{rm.rm_code}</p>
                          </div>
                        </td>
                        <td className="text-right p-4 text-white">{rm.dealers_count || 0}</td>
                        <td className="text-right p-4 text-white">{rm.active_clients || 0}</td>
                        <td className="text-right p-4 text-white">{formatCurrency(rm.current_aum)}</td>
                        <td className="text-right p-4 text-[#C5A059]">{formatCurrency(rm.mtd_revenue)}</td>
                        <td className="text-right p-4 text-emerald-500">{formatCurrency(rm.ytd_revenue)}</td>
                        <td className="text-right p-4">
                          <span className={`font-medium ${rm.achievement_percent >= 100 ? 'text-emerald-500' : rm.achievement_percent >= 75 ? 'text-[#C5A059]' : 'text-red-500'}`}>
                            {rm.achievement_percent || 0}%
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleRmDrilldown(rm)}
                            className="text-[#C5A059] hover:text-[#b4914e]"
                          >
                            View Trends
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Dealer Performance Tab */}
      <TabsContent value="dealer-performance" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Dealer Performance Overview</h2>
          <Button variant="outline" size="sm" onClick={fetchAllDealersPerformance}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {allDealersPerformance.length === 0 ? (
          <EmptyState 
            title="No Dealers Found"
            message="No Dealer Master records exist yet. Create dealers in the Dealer Master page to track their performance."
            icon={Building2}
          />
        ) : (
          <Card className="card-dark">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Dealer Name</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Reporting RM</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Active Clients</th>
                      <th className="text-right p-4 text-slate-400 font-medium">AUM</th>
                      <th className="text-right p-4 text-slate-400 font-medium">MTD Revenue</th>
                      <th className="text-right p-4 text-slate-400 font-medium">YTD Revenue</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Target %</th>
                      <th className="text-center p-4 text-slate-400 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDealersPerformance.map((dealer) => (
                      <tr 
                        key={dealer.dealer_code} 
                        className="border-b border-slate-800 hover:bg-slate-800/50"
                      >
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{dealer.name}</p>
                            <p className="text-xs text-slate-500">{dealer.dealer_code}</p>
                          </div>
                        </td>
                        <td className="p-4 text-slate-300">{dealer.reporting_rm_name || 'Direct to Principal'}</td>
                        <td className="text-right p-4 text-white">{dealer.active_clients || 0}</td>
                        <td className="text-right p-4 text-white">{formatCurrency(dealer.current_aum)}</td>
                        <td className="text-right p-4 text-[#C5A059]">{formatCurrency(dealer.mtd_revenue)}</td>
                        <td className="text-right p-4 text-emerald-500">{formatCurrency(dealer.ytd_revenue)}</td>
                        <td className="text-right p-4">
                          <span className={`font-medium ${dealer.achievement_percent >= 100 ? 'text-emerald-500' : dealer.achievement_percent >= 75 ? 'text-[#C5A059]' : 'text-red-500'}`}>
                            {dealer.achievement_percent || 0}%
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDealerDrilldown(dealer)}
                            className="text-[#C5A059] hover:text-[#b4914e]"
                          >
                            View Trends
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Scorecards Tab */}
      <TabsContent value="scorecards" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Dealer Scorecards</h2>
          <div className="flex items-center gap-2">
            <Select value={periodCode} onValueChange={setPeriodCode}>
              <SelectTrigger className="w-32 bg-slate-800 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const code = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return <SelectItem key={code} value={code}>{code}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            {isPrincipal && (
              <Button onClick={handleGenerateScorecards} className="bg-[#C5A059] hover:bg-[#b4914e]">
                <RefreshCcw className="w-4 h-4 mr-2" /> Generate
              </Button>
            )}
          </div>
        </div>

        {scorecards.length === 0 ? (
          <Card className="card-dark border-white/5">
            <CardContent className="py-10 text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No scorecards for this period.</p>
              <p className="text-sm text-slate-500 mt-2">
                Click Generate to create scorecards.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {scorecards.map((sc) => (
              <Card key={sc.id} className="card-dark border-white/5">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Dealer Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                        <span className="text-xl font-bold text-[#C5A059]">#{sc.composite?.rank}</span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{sc.dealer_name}</p>
                        <Badge className={`${getBandColor(sc.composite?.band)} text-white mt-1`}>
                          {sc.composite?.band}
                        </Badge>
                      </div>
                    </div>

                    {/* Achievement Summary */}
                    <div className="flex flex-wrap gap-4 lg:gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <TrendIcon trend={sc.trends?.brokerage} />
                          <span className="text-sm text-white">{sc.achievement?.brokerage_percent?.toFixed(0)}%</span>
                        </div>
                        <p className="text-xs text-slate-400">Brokerage</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <TrendIcon trend={sc.trends?.aum} />
                          <span className="text-sm text-white">{sc.achievement?.aum_percent?.toFixed(0)}%</span>
                        </div>
                        <p className="text-xs text-slate-400">AUM</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <TrendIcon trend={sc.trends?.clients} />
                          <span className="text-sm text-white">{sc.achievement?.active_clients_percent?.toFixed(0)}%</span>
                        </div>
                        <p className="text-xs text-slate-400">Clients</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <TrendIcon trend={sc.trends?.retention} />
                          <span className="text-sm text-white">{sc.achievement?.retention_rate_percent?.toFixed(0)}%</span>
                        </div>
                        <p className="text-xs text-slate-400">Retention</p>
                      </div>
                    </div>

                    {/* Score */}
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#C5A059]">{sc.composite?.total_score?.toFixed(1)}</p>
                      <p className="text-xs text-slate-400">Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      {/* Leaderboard Tab */}
      <TabsContent value="leaderboard" className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Dealer Leaderboard</h2>
          <Select value={periodCode} onValueChange={(v) => {
            setPeriodCode(v);
            setTimeout(fetchLeaderboard, 100);
          }}>
            <SelectTrigger className="w-32 bg-slate-800 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => {
                const d = new Date();
                d.setMonth(d.getMonth() - i);
                const code = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return <SelectItem key={code} value={code}>{code}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>

        {leaderboard.length === 0 ? (
          <Card className="card-dark border-white/5">
            <CardContent className="py-10 text-center">
              <Trophy className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No leaderboard data available.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-dark border-white/5">
            <CardContent className="p-0">
              <div className="divide-y divide-white/5">
                {leaderboard.map((entry, idx) => (
                  <div 
                    key={entry.id}
                    className={`flex items-center justify-between p-4 ${
                      idx < 3 ? 'bg-gradient-to-r from-[#C5A059]/10 to-transparent' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-yellow-500' :
                        idx === 1 ? 'bg-slate-400' :
                        idx === 2 ? 'bg-amber-700' :
                        'bg-slate-700'
                      }`}>
                        {idx < 3 ? (
                          <Trophy className={`w-5 h-5 ${idx === 0 ? 'text-yellow-900' : 'text-white'}`} />
                        ) : (
                          <span className="font-bold text-white">{entry.composite?.rank}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{entry.dealer_name}</p>
                        <Badge className={`${getBandColor(entry.composite?.band)} text-white text-xs`}>
                          {entry.composite?.band}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#C5A059]">{entry.composite?.total_score?.toFixed(1)}</p>
                        <p className="text-xs text-slate-400">Score</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-[#C5A059]" />
            Performance Management
          </h1>
          <p className="text-slate-400 mt-1">
            {isManagement ? 'Track KPIs, scorecards, and dealer rankings' : 'View your performance metrics and rankings'}
          </p>
        </div>
        {isPrincipal && (
          <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
            <Settings className="w-4 h-4 mr-2" /> Configure Weights
          </Button>
        )}
      </div>

      {/* Rule-based Notice */}
      <Card className="card-dark border-amber-500/20 bg-amber-500/5">
        <CardContent className="py-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-200">
            <strong>Rule-Based Performance:</strong> All KPIs are calculated using actual transaction and client data. No predictive AI is used.
          </p>
        </CardContent>
      </Card>

      {/* Main Content */}
      {isRM ? renderRMView() : (isDealer ? renderDealerView() : renderManagementView())}

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Scorecard Weight Configuration</DialogTitle>
            <DialogDescription>
              Adjust the weightage for composite score calculation. Total must equal 100%.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="flex justify-between">
                <span>Brokerage (max 40%)</span>
                <span className="text-[#C5A059]">{newWeights.brokerage}%</span>
              </Label>
              <Input
                type="range"
                min="0"
                max="40"
                value={newWeights.brokerage}
                onChange={(e) => setNewWeights({ ...newWeights, brokerage: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <Label className="flex justify-between">
                <span>AUM & Growth</span>
                <span className="text-[#C5A059]">{newWeights.aum}%</span>
              </Label>
              <Input
                type="range"
                min="0"
                max="50"
                value={newWeights.aum}
                onChange={(e) => setNewWeights({ ...newWeights, aum: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <Label className="flex justify-between">
                <span>Clients & Growth</span>
                <span className="text-[#C5A059]">{newWeights.clients}%</span>
              </Label>
              <Input
                type="range"
                min="0"
                max="50"
                value={newWeights.clients}
                onChange={(e) => setNewWeights({ ...newWeights, clients: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <Label className="flex justify-between">
                <span>Retention (min 10%)</span>
                <span className="text-[#C5A059]">{newWeights.retention}%</span>
              </Label>
              <Input
                type="range"
                min="10"
                max="30"
                value={newWeights.retention}
                onChange={(e) => setNewWeights({ ...newWeights, retention: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div className="pt-4 border-t border-white/5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Total</span>
                <span className={`font-bold ${
                  Math.abs(newWeights.brokerage + newWeights.aum + newWeights.clients + newWeights.retention - 100) < 0.1
                    ? 'text-emerald-500'
                    : 'text-red-500'
                }`}>
                  {newWeights.brokerage + newWeights.aum + newWeights.clients + newWeights.retention}%
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateConfig} className="bg-[#C5A059] hover:bg-[#b4914e]">
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drilldown Modal for RM/Dealer Trends */}
      <Dialog open={showDrilldownModal} onOpenChange={setShowDrilldownModal}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedRmForDrilldown ? (
                <>
                  <Users className="w-5 h-5 text-[#C5A059]" />
                  {selectedRmForDrilldown.name} - Performance Trends
                </>
              ) : selectedDealerForDrilldown ? (
                <>
                  <Building2 className="w-5 h-5 text-[#C5A059]" />
                  {selectedDealerForDrilldown.name} - Performance Trends
                </>
              ) : 'Performance Trends'}
            </DialogTitle>
            <DialogDescription>
              Month-on-month revenue and AUM trends (last 12 months)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Chart Type Toggle */}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant={chartType === 'line' ? 'default' : 'outline'}
                onClick={() => setChartType('line')}
                className={chartType === 'line' ? 'bg-[#C5A059]' : ''}
              >
                Line Chart
              </Button>
              <Button
                size="sm"
                variant={chartType === 'bar' ? 'default' : 'outline'}
                onClick={() => setChartType('bar')}
                className={chartType === 'bar' ? 'bg-[#C5A059]' : ''}
              >
                Bar Chart
              </Button>
            </div>

            {/* Revenue Trend */}
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Revenue Trend</h4>
              {drilldownTrends.length > 0 ? (
                <SimpleChart data={drilldownTrends} dataKey="revenue" label="Revenue" color="#C5A059" type={chartType} />
              ) : (
                <div className="h-32 flex items-center justify-center text-slate-500 bg-slate-800/50 rounded">
                  <div className="text-center">
                    <BarChart3 className="w-8 h-8 mx-auto mb-1 opacity-50" />
                    <p className="text-sm">No revenue data available</p>
                  </div>
                </div>
              )}
            </div>

            {/* AUM Trend */}
            <div>
              <h4 className="text-sm font-medium text-white mb-2">AUM Trend</h4>
              {drilldownTrends.length > 0 ? (
                <SimpleChart data={drilldownTrends} dataKey="aum" label="AUM" color="#10b981" type={chartType} />
              ) : (
                <div className="h-32 flex items-center justify-center text-slate-500 bg-slate-800/50 rounded">
                  <div className="text-center">
                    <DollarSign className="w-8 h-8 mx-auto mb-1 opacity-50" />
                    <p className="text-sm">No AUM data available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            {(selectedRmForDrilldown || selectedDealerForDrilldown) && (
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-700">
                <div className="text-center">
                  <p className="text-xs text-slate-400">Active Clients</p>
                  <p className="text-lg font-bold text-white">
                    {selectedRmForDrilldown?.active_clients || selectedDealerForDrilldown?.active_clients || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">YTD Revenue</p>
                  <p className="text-lg font-bold text-emerald-500">
                    {formatCurrency(selectedRmForDrilldown?.ytd_revenue || selectedDealerForDrilldown?.ytd_revenue)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Target Achievement</p>
                  <p className="text-lg font-bold text-[#C5A059]">
                    {selectedRmForDrilldown?.achievement_percent || selectedDealerForDrilldown?.achievement_percent || 0}%
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDrilldownModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformancePage;
